import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import RequestAccessModal from './RequestAccessModal';
import { ToolIcon } from '../home/tools/ToolTile';
import './requests.css';
import { toastSuccess, toastError, errorMessage } from '../../lib/toast';

// Sheets, documents, and dashboards are excluded — served via direct link/share,
// not access-grants. The server also enforces this on /api/tools/requestable.
const TOOL_TYPES = [
  { value: 'tool',    label: 'Tool' },
  { value: 'service', label: 'Service' },
];
const TOOL_TYPE_LABEL = Object.fromEntries(TOOL_TYPES.map((t) => [t.value, t.label]));

export default function RequestAccessTab() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [modal, setModal] = useState({ open: false, tool: null, opener: null });
  // After a request or cancel, the card whose actions changed takes focus back
  // (its old button is replaced, so the opener itself no longer exists).
  const [focusToolId, setFocusToolId] = useState(null);
  // ≤900px only: the rail is a closed disclosure there (the toggle is
  // display:none on desktop, where the rail is always open), like People.
  const [railOpen, setRailOpen] = useState(false);
  const activeFilterCount = (categoryFilter !== 'All' ? 1 : 0) + (typeFilter !== 'All' ? 1 : 0);

  // quiet: refresh in place after a request/cancel. Swapping the grid for
  // "Loading catalog…" unmounted every card, so focus fell to <body>.
  const load = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/tools/requestable`, { withCredentials: true });
      setTools(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (quiet) {
        // The action itself succeeded; keep the cards and say the view may be stale.
        toastError(errorMessage(err, "Couldn't refresh the catalog. Reload to see the latest."));
        return;
      }
      setError(errorMessage(err, "Couldn't load the tool catalog. Try again."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  const retry = () => load();

  const categories = useMemo(() => {
    const set = new Set();
    for (const t of tools) if (t.category) set.add(t.category);
    // "Uncategorized" only when something actually lacks a category.
    const hasUncategorized = tools.some((t) => !t.category);
    return ['All', ...[...set].sort(), ...(hasUncategorized ? ['Uncategorized'] : [])];
  }, [tools]);

  const filteredTools = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return tools.filter((t) => {
      const matchesSearch = !q
        || (t.name || '').toLowerCase().includes(q)
        || (t.description || '').toLowerCase().includes(q);
      const matchesCategory = categoryFilter === 'All'
        || (categoryFilter === 'Uncategorized' && !t.category)
        || t.category === categoryFilter;
      const matchesType = typeFilter === 'All' || (t.type || 'tool') === typeFilter;
      return matchesSearch && matchesCategory && matchesType;
    });
  }, [tools, searchQuery, categoryFilter, typeFilter]);

  const facetCounts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const searched = !q ? tools : tools.filter((t) =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
    const byCategory = { All: searched.length, Uncategorized: 0 };
    const byType = { All: searched.length };
    for (const t of searched) {
      const c = t.category || 'Uncategorized';
      byCategory[c] = (byCategory[c] || 0) + 1;
      const ty = t.type || 'tool';
      byType[ty] = (byType[ty] || 0) + 1;
    }
    return { byCategory, byType };
  }, [tools, searchQuery]);

  const handleRequestClick = (tool, opener) => {
    setModal({ open: true, tool, opener });
  };

  const handleCancelPending = async (tool) => {
    if (!tool.pending_request_id) return;
    if (!window.confirm(`Cancel your pending request for "${tool.name}"?`)) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/tool_access_requests/${tool.pending_request_id}`,
        { withCredentials: true }
      );
      toastSuccess('Request cancelled.');
      await load({ quiet: true });
      setFocusToolId(tool.id);
    } catch (err) {
      toastError(errorMessage(err, "Couldn't cancel your request. Try again."));
    }
  };

  const submitFromModal = async ({ tool, reason }) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/tool_access_requests`,
        { tool_id: tool.id, reason },
        { withCredentials: true }
      );
      const { auto_approved, status } = res.data || {};
      setModal({ open: false, tool: null, opener: null });
      if (auto_approved || status === 'approved') {
        toastSuccess(`Access granted — "${tool.name}" is now in My tools.`);
      } else {
        toastSuccess('Access request sent. An admin will review it.');
      }
      await load({ quiet: true });
      setFocusToolId(tool.id);
    } catch (err) {
      const map = {
        reason_required: 'Reason is required.',
        reason_too_long: 'Reason must be 500 characters or fewer.',
        already_granted: 'You already have access to this tool.',
        already_pending: 'You already have a pending request. Check My requests.',
        forbidden: "This tool is restricted, so you can't request it.",
        tool_not_found: 'That tool no longer exists. Refresh the page.',
        tool_not_requestable: "This tool can't be requested here. Ask its owner to share it with you.",
      };
      const message = errorMessage(err, "Couldn't send your request. Try again.", map);
      throw new Error(message);
    }
  };

  return (
    <div>
      {loading ? (
        <p className="rq-loading" role="status" aria-busy="true">Loading catalog…</p>
      ) : error ? (
        <div className="rq-error" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-secondary btn-small" onClick={retry}>Try again</button>
        </div>
      ) : (
        <div className="tools-catalog-shell">
          <aside className="tools-catalog-rail rq-catalog-rail" aria-label="Catalog filters">
            <button
              type="button"
              className="rq-catalog-rail__toggle"
              aria-expanded={railOpen}
              aria-controls="rq-catalog-rail-body"
              onClick={() => setRailOpen((v) => !v)}
            >
              <span>
                Filter tools
                {activeFilterCount > 0 && (
                  <span className="rq-catalog-rail__count"> · {activeFilterCount} active</span>
                )}
              </span>
              <svg className="rq-catalog-rail__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <path d="m9 6 6 6-6 6" />
              </svg>
            </button>
            <div id="rq-catalog-rail-body" className={`rq-catalog-rail__body${railOpen ? ' is-open' : ''}`}>
              <div className="tools-rail-group">
                <div className="tools-rail-head">Categories</div>
                <ul className="tools-rail-list">
                  {categories.map((c) => (
                    <li key={c}>
                      <button
                        type="button"
                        className={`tools-rail-row ${categoryFilter === c ? 'is-active' : ''}`}
                        aria-pressed={categoryFilter === c}
                        onClick={() => setCategoryFilter(c)}
                      >
                        <span>{c}</span>
                        <span className="tools-rail-count">{facetCounts.byCategory[c] || 0}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="tools-rail-group">
                <div className="tools-rail-head">Type</div>
                <ul className="tools-rail-list">
                  <li>
                    <button
                      type="button"
                      className={`tools-rail-row ${typeFilter === 'All' ? 'is-active' : ''}`}
                      aria-pressed={typeFilter === 'All'}
                      onClick={() => setTypeFilter('All')}
                    >
                      <span>All</span>
                      <span className="tools-rail-count">{facetCounts.byType.All || 0}</span>
                    </button>
                  </li>
                  {TOOL_TYPES.filter(({ value }) => (facetCounts.byType[value] || 0) > 0 || typeFilter === value).map(({ value, label }) => (
                    <li key={value}>
                      <button
                        type="button"
                        className={`tools-rail-row ${typeFilter === value ? 'is-active' : ''}`}
                        aria-pressed={typeFilter === value}
                        onClick={() => setTypeFilter(value)}
                      >
                        <span>{label}</span>
                        <span className="tools-rail-count">{facetCounts.byType[value] || 0}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {(categoryFilter !== 'All' || typeFilter !== 'All' || searchQuery) && (
                <button
                  type="button"
                  className="tools-rail-clear"
                  onClick={() => { setCategoryFilter('All'); setTypeFilter('All'); setSearchQuery(''); }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </aside>

          <div className="tools-catalog-main">
            {/* Stands alone like the People search: no bordered .filter-bar
                box around it (a box in a box beside the bordered rail). */}
            <input
              type="text"
              placeholder="Search tools by name or description…"
              aria-label="Search tools"
              className="search-input rq-catalog-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {filteredTools.length === 0 ? (
              <div className="rq-empty">
                <p className="rq-empty-title">No tools match your filters</p>
                <p>Try adjusting the search or clearing filters.</p>
              </div>
            ) : (
              <div className="tool-grid">
                {filteredTools.map((t) => (
                  <CatalogCard
                    key={t.id}
                    tool={t}
                    onRequest={(e) => handleRequestClick(t, e.currentTarget)}
                    onCancelPending={() => handleCancelPending(t)}
                    takeFocus={focusToolId === t.id}
                    onFocusTaken={() => setFocusToolId(null)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {modal.open && (
        <RequestAccessModal
          tool={modal.tool}
          returnFocusTo={modal.opener}
          onCancel={() => setModal({ open: false, tool: null, opener: null })}
          onSubmit={(reason) => submitFromModal({ tool: modal.tool, reason })}
        />
      )}
    </div>
  );
}

function CatalogCard({ tool, onRequest, onCancelPending, takeFocus, onFocusTaken }) {
  const status = tool.has_access
    ? 'owned'
    : tool.pending_request_id
      ? 'pending'
      : tool.last_rejected_at
        ? 'rejected'
        : 'available';

  // Once the refreshed data has changed this card's actions, focus its first
  // action (or the card itself when it has none, e.g. auto-approved → Owned).
  const cardRef = useRef(null);
  useEffect(() => {
    if (!takeFocus || !cardRef.current) return;
    const target = cardRef.current.querySelector('.tool-card-actions button') || cardRef.current;
    target.focus();
    onFocusTaken();
  }, [takeFocus, status, onFocusTaken]);

  return (
    <div className="tool-card" style={{ cursor: 'default' }} ref={cardRef} tabIndex={-1}>
      <div className="tool-card-header">
        <div className="tool-card-icon">
          <span className="rq-tool-icon rq-tool-icon--card"><ToolIcon tool={tool} /></span>
        </div>
        <div className="tool-card-info">
          <h4 className="tool-card-title" title={tool.name}>{tool.name}</h4>
          <div className="tool-card-meta">
            <span className="tool-card-category">{tool.category || 'Uncategorized'}</span>
            <span className={`tool-type-badge tool-type-badge--${tool.type || 'tool'}`}>
              {TOOL_TYPE_LABEL[tool.type] || 'Tool'}
            </span>
          </div>
        </div>
      </div>
      <p className="tool-card-desc">{tool.description}</p>

      {status === 'rejected' && tool.last_rejected_note && (
        <div className="rq-rejected-note" title={tool.last_rejected_note}>
          Last rejected: {tool.last_rejected_note}
        </div>
      )}

      <div className="tool-card-actions" style={{ marginTop: 4 }}>
        {status === 'owned' && (
          <span className="rq-card-chip rq-card-chip--owned">Owned</span>
        )}
        {status === 'pending' && (
          <>
            <span className="rq-card-chip rq-card-chip--pending">Pending review</span>
            <button
              type="button"
              className="btn btn-small btn-danger-outline"
              onClick={onCancelPending}
              aria-label={`Cancel request for ${tool.name}`}
            >
              Cancel
            </button>
          </>
        )}
        {(status === 'available' || status === 'rejected') && (
          tool.eligible ? (
            <button type="button" className="btn btn-primary btn-small" onClick={onRequest}>
              {status === 'rejected' ? 'Request again' : 'Request access'}
            </button>
          ) : (
            // Visible text, not a tooltip on a disabled button: touch and
            // keyboard users never see a title attribute.
            // "Restricted", not "Managers only": a manager of another
            // department is ineligible too (managers_only defaults are
            // requestable only by managers of a department that holds them).
            <span className="rq-card-chip rq-card-chip--muted">Restricted</span>
          )
        )}
      </div>
    </div>
  );
}
